// Room actions — Firestore write helpers for WhatsApp-style group rooms.
//
// A room lives at `rooms/{roomId}` with messages under
// `rooms/{roomId}/messages/{messageId}`. Dispatch/admin create + moderate
// rooms; any app user can be a member and post. Membership is stored as a
// `memberIds` array so a single `array-contains` listener feeds each user
// their rooms (see AppContext).
//
// Web port of mobile services/roomActions.ts — SAME Firestore layout + field
// names (both apps share one DB). The mobile file uses the modular SDK; this
// one uses the compat SDK the web app is built on (see services/firebase.ts),
// exactly like the writes in context/AppContext.tsx.

import firebase from 'firebase/compat/app';
import { ensureFirebaseInitialized } from './firebase';
import { OrderMention, UserRole } from '../types';
import { pruneMentions } from './orderMentions';
import { myReaction } from './roomReceipts';
import { USE_SUPABASE, supabase, isoFromMs, mustAffectRows } from './supabaseClient';
import { FEATURES } from './features';
import { uploadChatImage } from './chatMediaUpload';

// ── Supabase mode ───────────────────────────────────────────────────────────
// rooms / room_members / room_messages tables (supabase/migrations/0001).
// Membership lives in `room_members` rows (not a memberIds array); receipts
// stay the same {uid: epoch-ms} maps, stored as jsonb on the rooms row —
// values are epoch-ms NUMBERS (the unit the app compares against message
// timestamps), only real timestamptz columns use ISO strings.

/** Read-modify-write the rooms receipts jsonb — Postgres has no `merge` on a
 *  jsonb column through PostgREST, so we merge client-side.
 *  KNOWN LIMITATION (applies to every stamps write in this file): two clients
 *  merging concurrently can clobber each other's key between the select and
 *  the update — worst case a receipt reverts until that member next opens the
 *  room. The atomic fix is a `jsonb ||` RPC (see PENDING.md, supabase
 *  follow-ups); kept client-side for now to match the shipped pattern. */
async function sbMergeRoomStamps(
  roomId: string,
  userId: string,
  at: number,
  read: boolean
): Promise<void> {
  const sb = supabase();
  const { data, error } = await sb
    .from('rooms')
    .select('delivered_at, read_at')
    .eq('id', roomId)
    .single();
  if (error) throw new Error(`rooms read: ${error.message}`);
  const patch: Record<string, unknown> = {
    delivered_at: { ...(data?.delivered_at ?? {}), [userId]: at },
  };
  if (read) patch.read_at = { ...(data?.read_at ?? {}), [userId]: at };
  const { error: updErr } = await sb.from('rooms').update(patch).eq('id', roomId);
  if (updErr) throw new Error(`rooms update: ${updErr.message}`);
}

interface Performer {
  id: string;
  name: string;
  role: UserRole;
}

const rid = () => `room_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
const mid = () => `msg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

/** Create a room. The creator is always a member + admin. Returns the id. */
export async function createRoom(
  name: string,
  memberIds: string[],
  creator: Performer
): Promise<string> {
  if (USE_SUPABASE) {
    const sb = supabase();
    const id = rid();
    const members = Array.from(new Set([creator.id, ...memberIds]));
    const { error } = await sb.from('rooms').insert({
      id,
      name: name.trim(),
      created_by: creator.id,
      created_by_name: creator.name,
      created_at: isoFromMs(Date.now()),
    });
    if (error) throw new Error(`rooms insert: ${error.message}`);
    const { error: memErr } = await sb.from('room_members').insert(
      members.map((uid) => ({ room_id: id, user_id: uid, is_admin: uid === creator.id }))
    );
    if (memErr) throw new Error(`room_members insert: ${memErr.message}`);
    return id;
  }
  const { db } = ensureFirebaseInitialized();
  const id = rid();
  // De-dupe + guarantee the creator is included.
  const members = Array.from(new Set([creator.id, ...memberIds]));
  await db.collection('rooms').doc(id).set({
    id,
    name: name.trim(),
    createdBy: creator.id,
    createdByName: creator.name,
    createdAt: Date.now(),
    memberIds: members,
    adminIds: [creator.id],
  });
  return id;
}

/** Post a message to a room and update its list-preview fields. */
export async function sendRoomMessage(
  roomId: string,
  text: string,
  sender: Performer,
  imageUrl?: string,
  mentions?: OrderMention[],
  // Postgres only: the composer mints the message's final id up front so its
  // optimistic bubble and the row the realtime INSERT brings back are the
  // same message (services/optimisticChat.ts); a retry re-sends the same id.
  // `at` pins the timestamp the bubble already shows. Firestore ignores both.
  opts?: { id?: string; at?: number }
): Promise<void> {
  const body = text.trim();
  if (!body && !imageUrl) return;
  if (USE_SUPABASE) {
    const sb = supabase();
    const id = opts?.id ?? mid();
    const now = opts?.at ?? Date.now();
    const row: Record<string, unknown> = {
      id,
      room_id: roomId,
      body,
      // INVARIANT (keyset paging, services/chat/window.ts): created_at is
      // stamped HERE with millisecond precision (toISOString). Never fall back
      // to a now() default or a sub-ms backfill — the (created_at, id) cursor
      // tie-breaks same-instant siblings by id only when timestamps are
      // byte-equal. Index 0053 (room_id, created_at desc, id desc) serves it.
      created_at: isoFromMs(now),
      sender_id: sender.id,
      sender_name: sender.name,
      sender_role: sender.role,
    };
    if (imageUrl) {
      if (FEATURES.chatMediaV2 && imageUrl.startsWith('data:')) {
        // Phase 5: move the bytes into the chat-media bucket, store signed URLs.
        // This runs inside the optimistic outbox (RoomChat handleSend), so the
        // awaited upload does not block the composer; a retry re-sends the same
        // base64 with the same id → upsert overwrites the same object (idempotent).
        const up = await uploadChatImage(imageUrl, roomId, id);
        row.image_url = up.displayUrl;
        row.image_thumb_url = up.thumbUrl;
        row.image_path = up.path;
      } else {
        row.image_url = imageUrl; // flag OFF → base64, EXACTLY as before (also an already-URL retry)
      }
    }
    const kept = pruneMentions(body, mentions ?? []);
    if (kept.length) row.mentions = kept;
    const { error } = await sb.from('room_messages').insert(row);
    if (error) throw new Error(`room_messages insert: ${error.message}`);
    // Preview + self-receipt on the rooms row (same fields the Firestore
    // path merges below).
    const { data, error: readErr } = await sb
      .from('rooms')
      .select('delivered_at, read_at')
      .eq('id', roomId)
      .single();
    if (readErr) throw new Error(`rooms read: ${readErr.message}`);
    const { error: updErr } = await sb
      .from('rooms')
      .update({
        last_message_at: isoFromMs(now),
        last_message_body: body || '📷 Photo',
        last_message_sender_name: sender.name,
        last_message_sender_id: sender.id,
        // Your own message is delivered to + read by you the instant you send
        // it — same self-receipt the Firestore path merges below.
        delivered_at: { ...(data?.delivered_at ?? {}), [sender.id]: now },
        read_at: { ...(data?.read_at ?? {}), [sender.id]: now },
      })
      .eq('id', roomId);
    if (updErr) throw new Error(`rooms update: ${updErr.message}`);
    return;
  }
  const { db } = ensureFirebaseInitialized();
  const id = mid();
  const now = Date.now();
  const payload: Record<string, unknown> = {
    id,
    roomId,
    body,
    timestamp: now,
    senderId: sender.id,
    senderName: sender.name,
    senderRole: sender.role,
  };
  // Compressed base64 data URL — same shape the web OrderChat stores.
  // Lives inside the message doc itself, no Storage round-trip.
  if (imageUrl) payload.imageUrl = imageUrl;
  // Only written when the message actually references orders — keeps every
  // pre-mentions doc shape unchanged.
  const kept = pruneMentions(body, mentions ?? []);
  if (kept.length) payload.mentions = kept;
  await db.collection('rooms').doc(roomId).collection('messages').doc(id).set(payload);
  await db.collection('rooms').doc(roomId).set(
    {
      lastMessageAt: now,
      lastMessageBody: body || '📷 Photo',
      lastMessageSenderName: sender.name,
      lastMessageSenderId: sender.id,
      // Your own message is delivered to + read by you the instant you send
      // it. Stamping it here also stops the sender's own delivery watcher
      // (AppContext) from firing a redundant write for every message they post.
      deliveredAt: { [sender.id]: now },
      readAt: { [sender.id]: now },
    },
    { merge: true }
  );
}

/**
 * Mark every message up to `at` as DELIVERED to this member — their session
 * has the doc, whether or not they've opened the room. Called from the
 * app-wide rooms listener (context/AppContext.tsx), which is the closest web
 * analogue of WhatsApp's "the phone has it".
 *
 * `set(..., {merge:true})` rather than `update()` so a room doc written before
 * receipts shipped (no `deliveredAt` map) doesn't fail the write.
 */
export async function markRoomDelivered(
  roomId: string,
  userId: string,
  at: number
): Promise<void> {
  if (USE_SUPABASE) {
    await sbMergeRoomStamps(roomId, userId, at, false);
    return;
  }
  const { db } = ensureFirebaseInitialized();
  await db.collection('rooms').doc(roomId).set(
    { deliveredAt: { [userId]: at } },
    { merge: true }
  );
}

/** Is this room unread for `userId`? True when the room's newest message is
 *  past what the user has had ON SCREEN — readAt[uid], the same receipt that
 *  turns the sender's ticks blue, written by web RoomChat and the mobile app
 *  alike. Per-room, per-user, cross-device; drives the rooms list styling and
 *  the Groups tab badge. */
export function isRoomUnreadFor(
  room: {
    lastMessageAt?: number;
    lastMessageSenderId?: string;
    readAt?: Record<string, number>;
  },
  userId: string | null | undefined
): boolean {
  if (!userId || !room.lastMessageAt) return false;
  // Your own message is never unread to you — checking here (not just via
  // the sender's readAt stamp) stops the badge flashing on your OTHER
  // devices while that stamp is still syncing.
  if (room.lastMessageSenderId && room.lastMessageSenderId === userId) return false;
  return room.lastMessageAt > (room.readAt?.[userId] ?? 0);
}

/** Mark everything up to `at` as READ (blue ticks) by this member. Read
 *  implies delivery, so both marks move together. */
export async function markRoomRead(
  roomId: string,
  userId: string,
  at: number
): Promise<void> {
  if (USE_SUPABASE) {
    await sbMergeRoomStamps(roomId, userId, at, true);
    return;
  }
  const { db } = ensureFirebaseInitialized();
  await db.collection('rooms').doc(roomId).set(
    { deliveredAt: { [userId]: at }, readAt: { [userId]: at } },
    { merge: true }
  );
}

// ── Phase 2 (chatEngineV2): read state in room_member_state, via RPC ──────────
// Advance-only read cursor (services/../0054_room_member_state.sql), replacing
// the rooms.read_at jsonb read-modify-write race. Called ALONGSIDE markRoomRead
// during rollout so both stores stay current — old (flag-off) clients read the
// jsonb; the unread summary reads room_member_state.
/** The read cursor for "everything up to this millisecond": the LAST microsecond
 *  of it. The app keeps time in milliseconds but Postgres stamps in microseconds,
 *  so a message created at …11.583854 was NEWER than a cursor of …11.583 — read,
 *  yet counted unread for ever, on every device (staging, 2026-09-21). Any
 *  message the database stamped itself (no client timestamp) has such a tail. */
export function readCursorIso(atMs: number): string {
  return new Date(atMs).toISOString().replace(/\.(\d{3})Z$/, '.$1999Z');
}

export async function markRoomReadV2(roomId: string, atMs: number): Promise<void> {
  if (!USE_SUPABASE) return;
  const { error } = await supabase().rpc('mark_room_read', { p_room_id: roomId, p_at: readCursorIso(atMs) });
  if (error) throw new Error(`mark_room_read: ${error.message}`);
}

// One call → unread count for every room the caller is in (replaces the
// per-room COUNT storm in services/roomUnreadCounts.ts).
export async function fetchRoomUnreadSummary(): Promise<Array<{ roomId: string; unread: number }>> {
  if (!USE_SUPABASE) return [];
  const { data, error } = await supabase().rpc('room_unread_summary');
  if (error) throw new Error(`room_unread_summary: ${error.message}`);
  return (data || []).map((r: any) => ({ roomId: r.room_id, unread: Number(r.unread) || 0 }));
}

/**
 * Set (or clear) this user's reaction on a message. WhatsApp semantics: one
 * reaction per user per message, and tapping the one you already have removes
 * it. Pass `emoji: null` to clear outright.
 *
 * `current` is the reactions map as the caller last saw it — it tells us which
 * emoji to move the user OFF, and whether removing them empties a key. An
 * emptied key is DELETED rather than left as `[]`, so the doc never
 * accumulates dead emoji entries.
 */
export async function setMessageReaction(
  roomId: string,
  messageId: string,
  userId: string,
  emoji: string | null,
  current: Record<string, string[]> = {},
  // When present, ADDING/moving a reaction also bumps the room preview so the
  // other members get unread + a "reacted 👍" preview line, exactly like a
  // message would (per Umer, 2026-08-28). Clearing a reaction stays silent.
  activity?: { reactorName: string; snippet: string }
): Promise<void> {
  if (USE_SUPABASE) {
    const previous = myReaction(current, userId);
    // Tapping your existing reaction toggles it off.
    const next = emoji === previous ? null : emoji;
    if (!previous && !next) return;
    const sb = supabase();
    // Merge against the LIVE map (not the caller's snapshot) so a concurrent
    // reactor's entry survives — the Firestore path gets this for free from
    // arrayUnion/arrayRemove field transforms.
    const { data, error } = await sb
      .from('room_messages')
      .select('reactions')
      .eq('id', messageId)
      .single();
    if (error) throw new Error(`room_messages read: ${error.message}`);
    const reactions: Record<string, string[]> = {
      ...((data?.reactions as Record<string, string[]>) ?? {}),
    };
    if (previous) {
      const remaining = (reactions[previous] ?? []).filter((uid) => uid !== userId);
      // An emptied key is DELETED rather than left as [] — same as Firestore.
      if (remaining.length) reactions[previous] = remaining;
      else delete reactions[previous];
    }
    if (next) {
      reactions[next] = [...(reactions[next] ?? []).filter((uid) => uid !== userId), userId];
    }
    const { error: updErr } = await sb
      .from('room_messages')
      .update({ reactions: Object.keys(reactions).length ? reactions : null })
      .eq('id', messageId);
    if (updErr) throw new Error(`room_messages update: ${updErr.message}`);
    // ADDING/moving a reaction bumps the room preview + unread, exactly like
    // the Firestore path below. Clearing stays silent (`next` is null then).
    if (next && activity) {
      const now = Date.now();
      const { data: roomRow, error: roomErr } = await sb
        .from('rooms')
        .select('delivered_at, read_at')
        .eq('id', roomId)
        .single();
      if (roomErr) throw new Error(`rooms read: ${roomErr.message}`);
      const { error: bumpErr } = await sb
        .from('rooms')
        .update({
          last_message_at: isoFromMs(now),
          last_message_body: `Reacted ${next} to “${activity.snippet}”`,
          last_message_sender_name: activity.reactorName,
          last_message_sender_id: userId,
          // The reactor has obviously seen their own reaction.
          delivered_at: { ...(roomRow?.delivered_at ?? {}), [userId]: now },
          read_at: { ...(roomRow?.read_at ?? {}), [userId]: now },
        })
        .eq('id', roomId);
      if (bumpErr) throw new Error(`rooms update: ${bumpErr.message}`);
    }
    return;
  }
  const { db } = ensureFirebaseInitialized();
  const previous = myReaction(current, userId);
  // Tapping your existing reaction toggles it off.
  const next = emoji === previous ? null : emoji;
  if (!previous && !next) return;

  // Emoji are safe inside a dotted field path (no dots of their own), but
  // FieldPath keeps that guarantee independent of the emoji list.
  const path = (e: string) => new firebase.firestore.FieldPath('reactions', e);
  const pairs: Array<[firebase.firestore.FieldPath, unknown]> = [];

  if (previous) {
    const remaining = (current[previous] ?? []).filter((id) => id !== userId);
    pairs.push([
      path(previous),
      remaining.length
        ? firebase.firestore.FieldValue.arrayRemove(userId)
        : firebase.firestore.FieldValue.delete(),
    ]);
  }
  if (next) pairs.push([path(next), firebase.firestore.FieldValue.arrayUnion(userId)]);

  const [first, ...rest] = pairs;
  await db
    .collection('rooms')
    .doc(roomId)
    .collection('messages')
    .doc(messageId)
    .update(first[0], first[1], ...rest.flat());

  if (next && activity) {
    const now = Date.now();
    await db.collection('rooms').doc(roomId).set(
      {
        lastMessageAt: now,
        lastMessageBody: `Reacted ${next} to “${activity.snippet}”`,
        lastMessageSenderName: activity.reactorName,
        lastMessageSenderId: userId,
        // The reactor has obviously seen their own reaction.
        deliveredAt: { [userId]: now },
        readAt: { [userId]: now },
      },
      { merge: true }
    );
  }
}

export async function editRoomMessage(
  roomId: string,
  messageId: string,
  text: string,
  mentions?: OrderMention[]
): Promise<void> {
  const body = text.trim();
  if (!body) return;
  if (USE_SUPABASE) {
    const kept = pruneMentions(body, mentions ?? []);
    const { error } = await supabase()
      .from('room_messages')
      .update({
        body,
        edited: true,
        edited_at: isoFromMs(Date.now()),
        mentions: kept.length ? kept : null,
      })
      .eq('id', messageId);
    if (error) throw new Error(`room_messages update: ${error.message}`);
    return;
  }
  const { db } = ensureFirebaseInitialized();
  // An edit can delete a mention token. Re-prune against the new body and
  // actively remove the field when nothing survives, or the doc keeps
  // advertising a chip the text no longer contains.
  const kept = pruneMentions(body, mentions ?? []);
  await db.collection('rooms').doc(roomId).collection('messages').doc(messageId).update({
    body,
    edited: true,
    editedAt: Date.now(),
    mentions: kept.length ? kept : firebase.firestore.FieldValue.delete(),
  });
}

/**
 * Soft-delete a message: keep the doc as a tombstone (so the thread shows who
 * deleted it) but clear the body and drop the heavy base64 image payload.
 * When the deleted message was the room's latest, also rewind the room-list
 * preview so it doesn't keep advertising removed content.
 */
export async function deleteRoomMessage(
  roomId: string,
  messageId: string,
  deletedBy: Performer,
  wasLastMessage = false
): Promise<void> {
  if (USE_SUPABASE) {
    const sb = supabase();
    const { error } = await sb
      .from('room_messages')
      .update({
        deleted: true,
        deleted_at: isoFromMs(Date.now()),
        deleted_by_id: deletedBy.id,
        deleted_by_name: deletedBy.name,
        body: '',
        image_url: null,
        // Reactions belong to content that no longer exists.
        reactions: null,
      })
      .eq('id', messageId);
    if (error) throw new Error(`room_messages update: ${error.message}`);
    if (wasLastMessage) {
      const { error: roomErr } = await sb
        .from('rooms')
        .update({
          last_message_body: '🚫 Message deleted',
          last_message_sender_name: '',
          last_message_sender_id: '',
        })
        .eq('id', roomId);
      if (roomErr) throw new Error(`rooms update: ${roomErr.message}`);
    }
    return;
  }
  const { db } = ensureFirebaseInitialized();
  await db.collection('rooms').doc(roomId).collection('messages').doc(messageId).update({
    deleted: true,
    deletedAt: Date.now(),
    deletedById: deletedBy.id,
    deletedByName: deletedBy.name,
    body: '',
    imageUrl: firebase.firestore.FieldValue.delete(),
    // Reactions belong to content that no longer exists.
    reactions: firebase.firestore.FieldValue.delete(),
  });
  if (wasLastMessage) {
    await db.collection('rooms').doc(roomId).set(
      { lastMessageBody: '🚫 Message deleted', lastMessageSenderName: '', lastMessageSenderId: '' },
      { merge: true }
    );
  }
}

/** Add members (room admin only — enforced by the caller + rules). */
export async function addRoomMembers(
  roomId: string,
  memberIds: string[]
): Promise<void> {
  if (!memberIds.length) return;
  if (USE_SUPABASE) {
    // ignoreDuplicates = arrayUnion semantics: an existing member's row (and
    // its is_admin flag) is left untouched.
    const { error } = await supabase()
      .from('room_members')
      .upsert(
        memberIds.map((uid) => ({ room_id: roomId, user_id: uid })),
        { onConflict: 'room_id,user_id', ignoreDuplicates: true }
      );
    if (error) throw new Error(`room_members upsert: ${error.message}`);
    return;
  }
  const { db } = ensureFirebaseInitialized();
  await db.collection('rooms').doc(roomId).update({
    memberIds: firebase.firestore.FieldValue.arrayUnion(...memberIds),
  });
}

/** Remove a member (also strips any admin grant). */
export async function removeRoomMember(
  roomId: string,
  memberId: string
): Promise<void> {
  if (USE_SUPABASE) {
    // Dropping the row removes membership AND any admin grant in one go.
    const { error } = await supabase()
      .from('room_members')
      .delete()
      .eq('room_id', roomId)
      .eq('user_id', memberId);
    if (error) throw new Error(`room_members delete: ${error.message}`);
    return;
  }
  const { db } = ensureFirebaseInitialized();
  await db.collection('rooms').doc(roomId).update({
    memberIds: firebase.firestore.FieldValue.arrayRemove(memberId),
    adminIds: firebase.firestore.FieldValue.arrayRemove(memberId),
  });
}

/**
 * Promote a member to group admin, or dismiss one (contract item: admin
 * management, Umer 2026-09-15). Server-enforced: migration 0059's
 * `room_members_update` policy lets an admin-tier account, the room creator, or
 * an existing room admin flip `is_admin`; the caller gates the UI with
 * `canManageRoomMembers` so it never offers a button the database will reject.
 * The room creator must never be demoted — the caller guards that too, and it
 * is the one rule the server does not (a creator is not special in
 * room_members), so keep the client guard.
 *
 * Supabase: one UPDATE on the member's row. The rooms subscription has
 * room_members as a child, so adminIds recomputes and the UI reacts with no
 * extra work. Firestore: adminIds lives on the room doc (arrayUnion/Remove).
 */
export async function setRoomMemberAdmin(
  roomId: string,
  memberId: string,
  makeAdmin: boolean
): Promise<void> {
  if (USE_SUPABASE) {
    await mustAffectRows('room_members admin', () =>
      supabase()
        .from('room_members')
        .update({ is_admin: makeAdmin })
        .eq('room_id', roomId)
        .eq('user_id', memberId)
    );
    return;
  }
  const { db } = ensureFirebaseInitialized();
  await db.collection('rooms').doc(roomId).update({
    adminIds: makeAdmin
      ? firebase.firestore.FieldValue.arrayUnion(memberId)
      : firebase.firestore.FieldValue.arrayRemove(memberId),
  });
}

export async function renameRoom(roomId: string, name: string): Promise<void> {
  const trimmed = name.trim();
  if (!trimmed) return;
  if (USE_SUPABASE) {
    const { error } = await supabase().from('rooms').update({ name: trimmed }).eq('id', roomId);
    if (error) throw new Error(`rooms update: ${error.message}`);
    return;
  }
  const { db } = ensureFirebaseInitialized();
  await db.collection('rooms').doc(roomId).update({ name: trimmed });
}

/** Leave a room — same as removing yourself. */
export async function leaveRoom(roomId: string, userId: string): Promise<void> {
  await removeRoomMember(roomId, userId);
}

/** Delete the room doc. Orphaned message docs are left behind (unreachable
 *  without the parent) — acceptable for v1; a future cleanup can sweep them. */
export async function deleteRoom(roomId: string): Promise<void> {
  if (USE_SUPABASE) {
    // FK `on delete cascade` sweeps room_members + room_messages — no orphans
    // in this mode.
    const { error } = await supabase().from('rooms').delete().eq('id', roomId);
    if (error) throw new Error(`rooms delete: ${error.message}`);
    return;
  }
  const { db } = ensureFirebaseInitialized();
  await db.collection('rooms').doc(roomId).delete();
}
